import React, { useState } from 'react';
import { Button, Checkbox, Input, Modal, Popconfirm, Space, message } from 'antd';
import { completeAuditVisit, returnAuditVisit, startVisitReview } from '../../services/api';
import { getApiError } from '../../utils/apiError';

interface Props {
  visitId: string;
  status: string;
  isReviewer: boolean;
  onChanged: () => void;
}

const ReviewActions: React.FC<Props> = ({ visitId, status, isReviewer, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [meetingAmendment, setMeetingAmendment] = useState(false);

  if (!isReviewer) return null;

  const run = async (call: () => Promise<any>, done: string) => {
    setBusy(true);
    try {
      await call();
      message.success(done);
      setReturnOpen(false);
      setRemarks('');
      setMeetingAmendment(false);
      onChanged();
    } catch (err) {
      message.error(getApiError(err, 'That could not be done.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Space>
      {status === 'SUBMITTED' && (
        <Button type="primary" loading={busy} onClick={() => run(() => startVisitReview(visitId), 'Review started.')}>
          Start review
        </Button>
      )}
      {status === 'UNDER_REVIEW' && (
        <>
          <Button danger onClick={() => setReturnOpen(true)}>Return to auditors</Button>
          <Popconfirm
            title="Complete this review?"
            description="NCs are issued and the official rating is calculated. This cannot be undone."
            okText="Complete" onConfirm={() => run(() => completeAuditVisit(visitId), 'Visit completed and rated.')}
          >
            <Button type="primary" loading={busy}>Complete review</Button>
          </Popconfirm>
        </>
      )}
      <Modal
        title="Return the visit to the auditors"
        open={returnOpen}
        onCancel={() => setReturnOpen(false)}
        onOk={() => run(() => returnAuditVisit(visitId, remarks, meetingAmendment), 'Visit returned to the auditors.')}
        okText="Return" okButtonProps={{ disabled: !remarks.trim() }} confirmLoading={busy}
      >
        <Input.TextArea
          rows={4} value={remarks} onChange={(e) => setRemarks(e.target.value)}
          placeholder="What must the auditors correct or complete?"
        />
        <Checkbox
          style={{ marginTop: 12 }} checked={meetingAmendment}
          onChange={(e) => setMeetingAmendment(e.target.checked)}
        >
          The closing meeting record needs to be amended
        </Checkbox>
      </Modal>
    </Space>
  );
};

export default ReviewActions;
